import withLifecycle from '@hocs/with-lifecycle';
import { bi } from '@wix/media-manager-sdk';
import {
  anyPass,
  contains,
  equals,
  filter,
  head,
  isNil,
  join,
  map,
  not,
  once,
  pathOr,
  pick,
  pipe,
  prop,
  reject,
  uniq,
  without,
} from 'ramda';
import { contained } from 'ramda-adjunct';
import React, { createContext } from 'react';
import { connect } from 'react-refetch';
import { withRouter } from 'react-router';
import {
  branch,
  compose,
  renderNothing,
  setDisplayName,
  withContext,
  withProps,
  withPropsOnChange,
  withStateHandlers,
} from 'recompose';
import { MediaType } from '../../../constants/MediaType';
import {
  NAME_SPACE_ADD_MEDIA_BUTTON_TRANSLATIONS,
  NAME_SPACE_SITE_FILES_TRANSLATIONS,
  NameSpace,
} from '../../../constants/NameSpace';
import { PerfMark } from '../../../constants/PerfMark';
import { ExternalSource, MainSource, SupportedExternalSourcesList } from '../../../constants/Source';
import { WixMediaRoot } from '../../../constants/WixMediaRoot';
import { parentConnection, ParentConnectionEvent } from '../../../services/parentConnection';
import { getCiVersion } from '../../env/getCiVersion';
import { mark } from '../../env/performance';
import { isStandalone } from '../../env/utils';
import { getAdapter } from '../../media-manager-sdk/getAdapter';
import { isVisitorsUploads } from '../../media-manager-sdk/sources';
import { memoize } from '../../memoize';
import { parsePath } from '../../mmgrSettings/path';
import { removeUndefinedValues } from '../../object';
import { RouteBuilder, RouteMatcher } from '../../RouteHelpers';
import { parseSiteMediaToken, removeTemplatePart } from '../../siteMediaToken';
import { deserializeStringToArray } from '../../string';
import {
  createTrackMediaManagerLoadedSubscription,
  withTrackMediaManagerLoadedObservers,
} from '../bi/withAppLoadedAnalytics/withTrackMediaManagerLoadedObservers';
import { memoizeHandlers } from '../memoizeHandlers';
import { isValueChanged, onPropChange } from '../onPropChange';
import { withFirst, withoutFirst } from '../withFirst';
import { withLoadingErrorCatcher } from '../withLoadingErrorCatcher';
import { withoutRouter } from '../withoutRouter';
import { defaultMMGRSettings } from './defaultMMGRSettings';
import {
  getLocalConfig,
  isSecureContext,
  parseMaybeObject,
  parseMediaType,
  parseSettingsPath,
  parseTabsList,
  propsAreValid,
  selectTabsListForNamespace,
  trackMediaManagerOpened,
  validateIncomingSettings,
} from './utils';
import { withExperimentsSettings, withoutExperimentsSettings } from './withExperimentsSettings';
import {
  ConsentPolicyChangedMessage,
  contextTypes,
  IIncomingMMGRSettings,
  IMMGRSettingsProps,
  TIncomingTranslationsObject,
  TUserSettings,
} from './withMMGRSettings.types';
import { getInstance } from '../../hooks/useVideoMaker/getInstance';

const emitAppIsReady = once(() => parentConnection.emit(ParentConnectionEvent.Ready));

const fallbackUserSettings = {} as TUserSettings;

const createMediaManagerSDKBILoggingInitializer = memoize((siteMediaToken) =>
  once(() =>
    bi(true, {
      siteId: pipe(parseSiteMediaToken, prop('metaSiteId'), removeTemplatePart)(siteMediaToken),
      version: `MMGR6-${getCiVersion()}`,
    }),
  ),
);

export const MMGRContext = createContext<IMMGRSettingsProps>({
  mmgrSettings: null,
  latestIncomingMMGRSettings: null,
});

export const enhanceMMGRSettingsProvider = compose<IMMGRSettingsProps, {}>(
  setDisplayName('enhanceMMGRSettingsProvider'),
  withLoadingErrorCatcher,
  withExperimentsSettings,
  withProps(({ _experimentsSettings }) => ({
    extendedDefaultMMGRSettings: {
      ...defaultMMGRSettings,
      ..._experimentsSettings,
    },
  })),
  withoutExperimentsSettings,
  withFirst('appOpenedAnalytics'),
  withTrackMediaManagerLoadedObservers,
  withStateHandlers<IIncomingMMGRSettings, {}, Partial<IIncomingMMGRSettings> & { extendedDefaultMMGRSettings: any }>(
    ({ extendedDefaultMMGRSettings }) => ({
      latestIncomingMMGRSettings: getLocalConfig(),
      mmgrSettings: {
        ...extendedDefaultMMGRSettings,
        ...getLocalConfig(),
      },
      isFirstLoadingDone: false,
    }),
    {
      setMMGRSettings:
        (_, { extendedDefaultMMGRSettings, isFirst, setFirst, updateSettings$ }) =>
        (newMMGRSettings) => {
          validateIncomingSettings(newMMGRSettings);

          const mmgrSettings = {
            ...extendedDefaultMMGRSettings,
            ...getLocalConfig(),
            ...reject(anyPass([equals(''), isNil]), newMMGRSettings),
          };

          trackMediaManagerOpened({ isFirst, setFirst, mmgrSettings });

          updateSettings$.next();

          parentConnection.emit(ParentConnectionEvent.SettingsReady);
          return {
            latestIncomingMMGRSettings: newMMGRSettings,
            mmgrSettings,
          };
        },
      goToEmpty:
        ({ mmgrSettings }) =>
        () => ({ mmgrSettings: { ...mmgrSettings, path: RouteBuilder.Empty() } }),
      setFirstLoadingDone: () => (isFirstLoadingDone) => ({ isFirstLoadingDone }),
      updateConsentPolicy: () => (message: ConsentPolicyChangedMessage) => {
        if (!window.consentPolicyManager) {
          return;
        }
        window.consentPolicyManager.init({
          consentPolicy: message.data,
        });
      },
    },
  ),
  withLifecycle(() => {
    const addMarks = () => {
      mark(PerfMark.LoadStart);
      mark(PerfMark.OpenStart);
    };

    return {
      onDidMount({ updateSettings$, trackLoaded$, mmgrSettings, setMMGRSettings, goToEmpty, updateConsentPolicy }) {
        createTrackMediaManagerLoadedSubscription(updateSettings$, trackLoaded$);

        emitAppIsReady();
        parentConnection.on('settings', addMarks);
        parentConnection.on('settings', setMMGRSettings);
        parentConnection.on('empty', goToEmpty);
        parentConnection.on('consentPolicyChanged', updateConsentPolicy);
      },
      onWillUnmount({ setMMGRSettings, goToEmpty, updateConsentPolicy }) {
        parentConnection.off('settings', addMarks);
        parentConnection.off('settings', setMMGRSettings);
        parentConnection.off('empty', goToEmpty);
        parentConnection.off('consentPolicyChanged', updateConsentPolicy);
      },
    };
  }),
  withoutFirst,
  branch(pipe(propsAreValid, not), renderNothing),
  onPropChange<IMMGRSettingsProps>(
    (prevProps, nextProps) => prevProps.mmgrSettings.siteMediaToken !== nextProps.mmgrSettings.siteMediaToken,
    ({ mmgrSettings }) => {
      createMediaManagerSDKBILoggingInitializer(mmgrSettings.siteMediaToken)();
    },
  ),
  connect<any>(({ mmgrSettings, setLoadingError }) => {
    const { siteMediaToken } = mmgrSettings;

    const privateSiteAdapter = getAdapter(MainSource.SiteFiles, mmgrSettings);

    return {
      loadUserSettings: () => ({
        userSettings: {
          value: () => {
            privateSiteAdapter.correlationId.randomize();

            return privateSiteAdapter.user.getSitesInfo().then((response) => {
              const {
                sitesNumber,
                hasVisitorUploads,
                hasContributors,
                hasWixVideoUploads,
                team,
                country,
                parentAccountId,
                enterpriseTabs,
              } = response.data;

              return {
                parentAccountId,
                hasVisitorUploads,
                hasContributors,
                sitesNumber,
                hasWixVideoUploads,
                team,
                hasMultipleSites: sitesNumber > 1,
                country,
                enterpriseTabs,
              };
            });
          },
          comparison: siteMediaToken,
          andCatch: (error) => {
            console.error(error);
            setLoadingError(error);

            return {};
          },
          force: true,
        },
      }),
    };
  }),
  memoizeHandlers('loadUserSettings'),
  connect<any>(({ mmgrSettings }) => {
    const { metaSiteToken, siteMediaToken } = mmgrSettings;
    return {
      getMetaSiteToken: () => {
        if (metaSiteToken) {
          return { fetchedMetaSiteToken: { value: metaSiteToken } };
        }
        const fetchInstanceToken = getInstance(parseSiteMediaToken(siteMediaToken).metaSiteId);
        return { fetchedMetaSiteToken: { value: fetchInstanceToken() } };
      },
    };
  }),
  memoizeHandlers('getMetaSiteToken'),
  withLifecycle({
    onDidMount({ loadUserSettings, getMetaSiteToken }) {
      loadUserSettings();
      getMetaSiteToken();
    },
    onDidUpdate(
      { loadingError: wasLoadingError },
      { loadUserSettings, getMetaSiteToken, loadingError, userSettings, setFirstLoadingDone },
    ) {
      if (wasLoadingError && !loadingError) {
        loadUserSettings();
        getMetaSiteToken();
      }

      if (userSettings.fulfilled) {
        setFirstLoadingDone(true);
      }
    },
  }),
  branch(
    ({ userSettings, isFirstLoadingDone }) => (!userSettings || userSettings.pending) && !isFirstLoadingDone,
    renderNothing,
  ),
  withProps(({ userSettings }) => ({
    // use fallbackUserSettings to save reference to object, so mmgrSettings won't be updated
    // if user receives error and click "Try Again" button, which in this case sends 600 evid
    userSettings: pathOr<TUserSettings, TUserSettings>(fallbackUserSettings, ['value'], userSettings),
  })),
  withProps(({ fetchedMetaSiteToken }) => ({
    fetchedMetaSiteToken: pathOr(null, ['value'], fetchedMetaSiteToken),
  })),
  withRouter,
  withPropsOnChange(
    ['mmgrSettings', 'fetchedMetaSiteToken'],
    ({ mmgrSettings, userSettings, fetchedMetaSiteToken, location }) => {
      const translations: TIncomingTranslationsObject = parseMaybeObject(mmgrSettings.translations);

      const { namespace, publicMediaRoot } = mmgrSettings;

      const isGdprMode = RouteMatcher.isAll(location.pathname) || namespace === NameSpace.All;

      const isOpenedInSecureContext = isSecureContext(namespace) || isGdprMode;

      const hasVisitorUploads = userSettings.hasVisitorUploads;

      const parsedTabList = pipe<string, MainSource[], MainSource[]>(
        deserializeStringToArray,
        parseTabsList,
      )(mmgrSettings.tabsList);

      const excludedTabs = [];
      const tabsToReplace = {};

      if (isOpenedInSecureContext) {
        excludedTabs.push(MainSource.Home);
      }

      if (!isOpenedInSecureContext && contains(MainSource.Favorites, parsedTabList)) {
        tabsToReplace[MainSource.Favorites] = MainSource.Boards;
      }

      const tabsList = isOpenedInSecureContext
        ? selectTabsListForNamespace(namespace)
        : pipe<MainSource[], MainSource[], MainSource[]>(
            without(excludedTabs),
            map((oldTab) => (tabsToReplace[oldTab] ? tabsToReplace[oldTab] : oldTab)),
          )(parsedTabList);

      const shouldAddShapes =
        join(',', deserializeStringToArray<MediaType>(mmgrSettings.mediaType)) === 'site_icon,picture';
      const mediaTypeWithShapes = `${mmgrSettings.mediaType},shape`;
      const mediaType = parseMediaType(namespace, shouldAddShapes ? mediaTypeWithShapes : mmgrSettings.mediaType);

      const gdprSettings = isGdprMode
        ? {
            addMediaButtonVisible: false,
            canSubmitItems: false,
            namespace: NameSpace.All,
            path: RouteBuilder.All(),
          }
        : {};

      const parsedPublicMediaRoot = deserializeStringToArray<WixMediaRoot>(publicMediaRoot);
      const path = mmgrSettings.path
        ? parseSettingsPath(mmgrSettings.path, tabsList, head(parsedPublicMediaRoot))
        : RouteBuilder.Source(head(tabsList));
      const pathSource = parsePath(path).source;

      const initialMediaTypes = deserializeStringToArray<MediaType>(mmgrSettings.initialMediaTypes);

      const servicesList = pipe<string, ExternalSource[], ExternalSource[]>(
        deserializeStringToArray,
        filter(contained(SupportedExternalSourcesList)),
      )(mmgrSettings.servicesList);
      const defaultServicesList = deserializeStringToArray<ExternalSource>(defaultMMGRSettings.servicesList);
      const externalSources = [ExternalSource.Computer, ...filter(contained(servicesList), defaultServicesList)];

      return {
        mmgrSettings: {
          ...mmgrSettings,
          ...userSettings,
          maxParallelUploads: Number(mmgrSettings.maxParallelUploads),
          isSecureContext: isOpenedInSecureContext,
          mediaType,
          selection: mmgrSettings.selectionType,
          tabsList,
          externalSources,
          vision: uniq(['label', 'face', ...deserializeStringToArray(mmgrSettings.vision)]),
          path,
          publicMediaRoot: parsedPublicMediaRoot,
          hasVisitorUploads: hasVisitorUploads || isVisitorsUploads(pathSource),
          canSubmitItems: mmgrSettings.canShowSelectButton,
          addMediaButtonVisible: mmgrSettings.uploadItems,
          addMediaPanelOpened: mmgrSettings.uploadItemsOpen,
          servicesList,
          initialMediaTypes,
          translations: removeUndefinedValues({
            'common.header': translations.header,
            'actions.choose-files': translations['footer.submit'],
            ...translations,
          }),
          addMediaButtonTranslationId: isOpenedInSecureContext
            ? NAME_SPACE_ADD_MEDIA_BUTTON_TRANSLATIONS[namespace]
            : 'categories.add-media',
          siteFilesTranslationId: isOpenedInSecureContext
            ? NAME_SPACE_SITE_FILES_TRANSLATIONS[namespace]
            : 'categories.site-files',
          metaSiteToken: fetchedMetaSiteToken,
          ...gdprSettings,
        },
      };
    },
  ),
  onPropChange(
    isValueChanged(({ mmgrSettings }) => mmgrSettings.path),
    ({ mmgrSettings, history }) => {
      history.replace(mmgrSettings.path);
    },
    isStandalone,
  ),
  withContext(contextTypes, pick(['mmgrSettings', 'latestIncomingMMGRSettings'])),
  withoutRouter,
);

export const MMGRSettingsProvider = enhanceMMGRSettingsProvider(
  ({ mmgrSettings, latestIncomingMMGRSettings, children }) => (
    <MMGRContext.Provider value={{ mmgrSettings, latestIncomingMMGRSettings }}>{children}</MMGRContext.Provider>
  ),
);

export const withMMGRSettingsProvider = (BaseComponent) => (props) =>
  (
    <MMGRSettingsProvider>
      <BaseComponent {...props} />
    </MMGRSettingsProvider>
  );
